import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import feathersClient from '../../../../apiClient';
import { VodafoneTariffData } from '@tymbe/schema/vodafone-tariff.interface';
import { VodafoneTariffState } from '@tymbe/schema/enums';
import {
  TyTable,
  TyTableRow,
  TyTablePagination,
  TyTableSortHeader,
  NoDataTableRow,
} from '@tymbe/ty-components/table';
import defaultPageSizeOptions from '../../../../components/Table/table.utils';
import { Form } from 'informed';
import useURLParamsHandler from '../../../../hooks/UrlParamsHandler/useURLParamsHandler';
import { vodafoneTariffHeaders, getHeadersForTab } from './headers/vodafoneTariffHeaders';
import { VodafoneTariffRow } from './VodafoneTariffRow';
import { VodafoneTariffDetailModal } from './VodafoneTariffDetailModal';
import { toast } from 'react-toastify';
import { getPaginatedResponse } from '../../../../apiClient/utils';
import { POSTGRES_INT_MAX, POSTGRES_INT_MIN } from '@tymbe/schema/constants/database_constants';

type Props = {
  queryId: string;
  tabName: string;
} & (
    | { state: VodafoneTariffState; modifier?: never }
    | { state?: never; modifier: string }
  );

export const VodafoneTariffTable = ({ queryId, state, modifier, tabName }: Props) => {
  const [{ allSearchParams }, updateQueryParams] = useURLParamsHandler();
  const [selectedTariffId, setSelectedTariffId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const search = allSearchParams.search;
  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const pageSize = searchParams.get('pageSize') ? Number(searchParams.get('pageSize')) : defaultPageSizeOptions[0].value;
  const sort = allSearchParams.sort ?? { created_at: -1 };
  const queryClient = useQueryClient();

  // Get headers specific to the current tab
  const headersForTab = getHeadersForTab(tabName);

  const buildSearchQuery = (searchTerm: string) => {
    if (!searchTerm) return {};

    const searchConditions: any[] = [
      { phone_number: { $like: `%${searchTerm.trim()}%` } },
      { email: { $like: `%${searchTerm}%` } },
      { first_name: { $like: `%${searchTerm}%` } },
      { last_name: { $like: `%${searchTerm}%` } },
      { note: { $like: `%${searchTerm}%` } },
    ];

    // Handle integer fields - only search if the search term is a valid PostgreSQL integer
    const numericSearchTerm = parseInt(searchTerm, 10);
    const isValidPgInteger = !isNaN(numericSearchTerm) && 
                            numericSearchTerm <= POSTGRES_INT_MAX && 
                            numericSearchTerm >= POSTGRES_INT_MIN;

    if (isValidPgInteger) {
      searchConditions.push(
        { id: numericSearchTerm },
        { person_id: numericSearchTerm },
        { fakturoid_subject_id: numericSearchTerm },
        { fakturoid_proforma_id: numericSearchTerm }
      );
    }

    return {
      $or: searchConditions,
    };
  };

  const { data, isLoading } = useQuery(
    [queryId, { search, currentPage, pageSize, sort, state, modifier, page: searchParams.get('page'), pageSizeParam: searchParams.get('pageSize') }],
    async () => {
      const searchQuery = buildSearchQuery(search || '');
      const query: any = {
        ...searchQuery,
        $limit: pageSize,
        $skip: pageSize * (currentPage - 1),
        $sort: sort,
      };

      if (state) {
        query.state = state;
      }

      if (modifier) {
        query.$modify = [modifier];
      }

      const result = await feathersClient.service('vodafone-tariff').find({
        query,
      });
      return getPaginatedResponse(result);
    }
  );

  const { mutateAsync: updateState } = useMutation(
    async ({ id, newState, transferDate, phoneNumber, transferRequested }: { id: number; newState: VodafoneTariffState; transferDate?: string | null; phoneNumber?: string | null; transferRequested?: string | null }) => {
      const updateData: { state: VodafoneTariffState; transfer_date?: string | null; phone_number?: string | null; transfer_requested?: string | null } = { state: newState };
      if (transferDate !== undefined) {
        updateData.transfer_date = transferDate;
      }
      if (phoneNumber !== undefined) {
        updateData.phone_number = phoneNumber;
      }
      if (transferRequested !== undefined) {
        updateData.transfer_requested = transferRequested;
      }
      await feathersClient.service('vodafone-tariff').patch(id, updateData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryId);
        // Invalidate all vodafone tariff related queries to update counters
        queryClient.invalidateQueries('admin/vodafoneTariff');
        toast.success('Stav byl úspěšně změněn');
      },
      onError: (error) => {
        toast.error('Chyba při změně stavu');
        console.error('Error updating vodafone tariff state:', error);
      },
    }
  );

  const handleStateChange = async (id: number, newState: VodafoneTariffState, transferDate?: string | null, phoneNumber?: string | null, transferRequested?: string | null) => {
    await updateState({ id, newState, transferDate, phoneNumber, transferRequested }).catch();
  };

  const handleRowClick = (id: number) => {
    setSelectedTariffId(id);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTariffId(null);
  };

  return (
    <Form onSubmit={() => { /* Empty form handler */ }}>
      <TyTable>
        <thead>
          <TyTableRow>
            {headersForTab.map((header) => (
              <TyTableSortHeader
                key={header.value}
                field={header.sortable ? header.value : undefined}
                onSort={header.sortable ? (field: string, sortOrder: number) => {
                  updateQueryParams({
                    sort: { [field]: sortOrder },
                    page: 1,
                  });
                } : undefined}
              >
                {header.label}
              </TyTableSortHeader>
            ))}
          </TyTableRow>
        </thead>
        <tbody>
          {isLoading && (
            <NoDataTableRow colSpan={headersForTab.length}>
              Načítám...
            </NoDataTableRow>
          )}
          {!isLoading && (!data?.data || data.data.length === 0) && (
            <NoDataTableRow colSpan={headersForTab.length}>
              Žádné záznamy nenalezeny
            </NoDataTableRow>
          )}
          {data?.data?.map((vodafoneTariff: VodafoneTariffData) => (
            <VodafoneTariffRow
              key={vodafoneTariff.id}
              vodafoneTariff={vodafoneTariff}
              visibleColumns={headersForTab.map(header => header.value)}
              tabName={tabName}
              onStateChange={handleStateChange}
              onRowClick={handleRowClick}
            />
          ))}
        </tbody>
      </TyTable>

      <TyTablePagination
        rowsCount={data?.total || 0}
        pageSizeOptions={defaultPageSizeOptions}
      />

      <VodafoneTariffDetailModal
        tariffId={selectedTariffId}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
      />
    </Form>
  );
};
